// Voice mode's bar, in place of the composer while it is on (Kareem, 4 Nov: "the conversation is the screen"): a
// slim bar with a small live orb (it follows your voice), one word for what is happening (Listening, Thinking,
// Speaking) and one Stop. Speaking over the reply interrupts it (or tap the orb); there is no Mute, Interrupt, Replay
// or End row. The conversation above it is the same chat: your words in a live bubble, the reply streaming in, cards
// in one row with Approve. "Tap to hear" shows only when the browser will not play the reply without a tap.
import { motion } from 'motion/react';
import { spring } from '../lib/motion';
import { Orb } from '../ui/motion';
import { type useVoiceMode, type VoiceState } from '../voice/useVoiceMode';

type Voice = ReturnType<typeof useVoiceMode>;

/** The bar's one word: hearing you is still Listening. */
export const BAR_WORD: Record<VoiceState, string> = {
  off: 'Voice', starting: 'Listening', listening: 'Listening', hearing: 'Listening', thinking: 'Thinking', speaking: 'Speaking', blocked: 'Reply ready',
};

/** The control next to the microphone that starts voice mode. */
export function VoiceModeButton({ voice, phone, disabled }: { voice: Voice; phone: boolean; disabled?: boolean }) {
  return phone
    ? <button type="button" className="mic vmode" aria-label="Voice mode" title="Voice mode: talk it through, hands free" disabled={disabled} onClick={voice.start}><IconVoice /></button>
    : <button type="button" className="vmodeb" aria-label="Voice mode" title="Voice mode: talk it through, hands free" disabled={disabled} onClick={voice.start}><IconVoice /><span>Voice</span></button>;
}

export function VoiceBar({ voice, phone }: { voice: Voice; phone: boolean }) {
  const st = voice.state;
  const orb = st === 'thinking' ? 'thinking' : st === 'listening' || st === 'hearing' || st === 'starting' ? 'listening' : 'idle';
  return (
    <motion.div className={`vbar slim${phone ? ' phone' : ''}`} role="group" aria-label="Voice mode" data-state={st} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0, transition: spring.gentle }}>
      <button type="button" className="vorb" aria-label={st === 'speaking' ? 'Interrupt' : BAR_WORD[st]} onClick={() => (st === 'speaking' ? voice.interrupt() : st === 'blocked' ? voice.tapToHear() : undefined)}>
        <Orb size={phone ? 26 : 22} state={orb} level={voice.level} />
      </button>
      <div className="vt" aria-live="polite">
        <b>{BAR_WORD[st]}</b>
        {voice.error ? <span className="ve">{voice.error}</span> : null}
      </div>
      {st === 'blocked' && <button type="button" className="bt" onClick={voice.tapToHear}>Tap to hear</button>}
      <StopButton label="Stop voice mode" onClick={voice.end} />
    </motion.div>
  );
}

/** Stop, as a small round icon with a square (Kareem, 4 Nov: not a big black block). A 44px target on a phone. */
export function StopButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button type="button" className="vstopb" aria-label={label} title={label} onClick={onClick}><span><svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" /></svg></span></button>;
}

export const IconVoice = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true"><path d="M4 10v4M8 7v10M12 4v16M16 7v10M20 10v4" /></svg>;
